// store/slices/cartSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface CartItem {
  id: string;
  courseId: string;
  name: string;
  description: string;
  price: number;
  affiliateName: string;
  imageUrl?: string;
  quantity: number;
}

export interface CartState {
  items: CartItem[];
  total: number;
  itemCount: number;
  isOpen: boolean;
}

const initialState: CartState = {
  items: [],
  total: 0,
  itemCount: 0,
  isOpen: false,
};

/**
 * Calcula total do carrinho
 */
const calculateTotal = (items: CartItem[]): number => {
  return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
};

/**
 * Calcula quantidade de itens
 */
const calculateItemCount = (items: CartItem[]): number => {
  return items.reduce((sum, item) => sum + item.quantity, 0);
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    /**
     * Adiciona item ao carrinho
     */
    addToCart: (state, action: PayloadAction<Omit<CartItem, 'quantity'>>) => {
      const existingItem = state.items.find(item => item.courseId === action.payload.courseId);

      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        state.items.push({
          ...action.payload,
          quantity: 1,
        });
      }

      state.total = calculateTotal(state.items);
      state.itemCount = calculateItemCount(state.items);
    },

    /**
     * Remove item do carrinho
     */
    removeFromCart: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.courseId !== action.payload);
      state.total = calculateTotal(state.items);
      state.itemCount = calculateItemCount(state.items);
    },

    /**
     * Atualiza quantidade de um item
     */
    updateItemQuantity: (state, action: PayloadAction<{ courseId: string; quantity: number }>) => {
      const item = state.items.find(item => item.courseId === action.payload.courseId);

      if (item) {
        if (action.payload.quantity <= 0) {
          state.items = state.items.filter(item => item.courseId !== action.payload.courseId);
        } else {
          item.quantity = action.payload.quantity;
        }
      }

      state.total = calculateTotal(state.items);
      state.itemCount = calculateItemCount(state.items);
    },

    /**
     * Limpa carrinho
     */
    clearCart: (state) => {
      state.items = [];
      state.total = 0;
      state.itemCount = 0;
    },

    /**
     * Toggle sidebar do carrinho
     */
    toggleCart: (state) => {
      state.isOpen = !state.isOpen;
    },

    /**
     * Abre carrinho
     */
    openCart: (state) => {
      state.isOpen = true;
    },

    /**
     * Fecha carrinho
     */
    closeCart: (state) => {
      state.isOpen = false;
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  updateItemQuantity,
  clearCart,
  toggleCart,
  openCart,
  closeCart,
} = cartSlice.actions;

export default cartSlice.reducer;
